<template>
  <validation-observer ref="refFormObserver">
    <b-form slot-scope="{ validate }" @submit.prevent="validate().then(onSubmit)">
      <!-- Sticky Form Header -->
      <div
        class="px-2 rounded-sm resource-header border-bottom d-flex justify-content-end align-items-start mb-1 sticky-top bg-white"
        style="z-index: 1000; padding: 1rem 0"
      >
        <div class="px-2 actions-container d-flex justify-content-between w-100">
          <div>
            <h4 class="mb-0">{{ formData && formData.id ? 'Edit Item' : 'Add New Item' }}</h4>
            <small class="text-muted">{{
              formData && formData.id ? 'Update item information' : 'Create a new item record'
            }}</small>
          </div>
          <div>
            <b-button
              type="button"
              variant="outline-default"
              class="mr-1"
              :disabled="loading"
              @click="isModal ? $emit('cancel') : $router.go(-1)"
            >
              Cancel
            </b-button>
            <b-button type="submit" variant="primary" :disabled="loading">
              <b-spinner small v-if="loading" class="mr-1"></b-spinner>
              {{ loading ? 'Saving...' : formData && formData.id ? 'Update Item' : 'Save Item' }}
            </b-button>
          </div>
        </div>
      </div>

      <!-- Basic Information Card -->
      <b-card no-body>
        <b-card-header>
          <b-card-title>Basic Information</b-card-title>
        </b-card-header>

        <b-card-body>
          <b-row>
            <!-- Item Name -->
            <b-col md="6">
              <form-input
                v-model="itemsFormData.itemName"
                :disabled="loading"
                label="Item Name"
                name="Item Name"
                rules="required"
                placeholder="Enter item name"
              />
            </b-col>

            <!-- Unit -->
            <b-col md="6">
              <UOMDropdownField
                v-model="itemsFormData.unitID"
                :disabled="loading || isLoadingUnits"
                label="Unit"
                name="Unit"
                rules="required"
                placeholder="Select unit"
              />
            </b-col>

            <!-- Item Group -->
            <b-col md="6">
              <ItemGroupDropdownField
                v-model="itemsFormData.itemGroupID"
                :disabled="loading || isLoadingItemGroups"
                label="Item Group"
                name="Item Group"
                placeholder="Select item group"
              />
            </b-col>

            <!-- HS Code -->
            <b-col md="6">
              <HSCodeDropdownField
                v-model="itemsFormData.itemHSCodeID"
                :disabled="loading || isLoadingHSCodes"
                label="HS Code"
                name="HS Code"
                rules="required"
                placeholder="Select HS code"
              />
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <!-- Pricing Information Card -->
      <b-card no-body>
        <b-card-header>
          <b-card-title>Pricing Information</b-card-title>
        </b-card-header>

        <b-card-body>
          <b-row>
            <!-- Purchase Rate -->
            <b-col md="6">
              <form-input
                v-model.number="itemsFormData.itemPurchaseRate"
                :disabled="loading"
                label="Purchase Rate"
                name="Purchase Rate"
                rules="numeric"
                type="number"
                min="0"
                step="0.01"
                placeholder="Enter purchase rate"
              />
            </b-col>

            <!-- Trade Price -->
            <b-col md="6">
              <form-input
                v-model.number="itemsFormData.tradePrice"
                :disabled="loading"
                label="Trade Price"
                name="Trade Price"
                rules="required|numeric"
                type="number"
                min="0"
                step="0.01"
                placeholder="Enter trade price"
              />
            </b-col>

            <!-- Retail Price -->
            <b-col md="6">
              <form-input
                v-model.number="itemsFormData.retailPrice"
                :disabled="loading"
                label="Retail Price"
                name="Retail Price"
                rules="required|numeric"
                type="number"
                min="0"
                step="0.01"
                placeholder="Enter retail price"
              />
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <!-- Tax Information Card -->
      <b-card no-body>
        <b-card-header>
          <b-card-title>Tax Information</b-card-title>
        </b-card-header>

        <b-card-body>
          <b-row>
            <!-- Sales Tax Percent -->
            <b-col md="6">
              <form-input
                v-model.number="itemsFormData.salesTaxPercent"
                :disabled="loading"
                label="Sales Tax %"
                name="Sales Tax %"
                rules="required|numeric"
                type="number"
                min="0"
                max="100"
                step="0.01"
                placeholder="Enter sales tax percentage"
              />
            </b-col>

            <!-- Additional Sales Tax Percent -->
            <b-col md="6">
              <form-input
                v-model.number="itemsFormData.addSalesTaxPercent"
                :disabled="loading"
                label="Additional Sales Tax %"
                name="Additional Sales Tax %"
                rules="required|numeric"
                type="number"
                min="0"
                max="100"
                step="0.01"
                placeholder="Enter additional sales tax percentage"
              />
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <!-- Status Card -->
      <b-card no-body>
        <b-card-header>
          <b-card-title>Status</b-card-title>
        </b-card-header>

        <b-card-body>
          <b-row>
            <b-col md="12">
              <b-form-group>
                <b-form-checkbox
                  id="item-active"
                  v-model="itemsFormData.isActive"
                  name="item-active"
                  value="true"
                  unchecked-value="false"
                  :disabled="loading"
                >
                  Active
                </b-form-checkbox>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>
    </b-form>
  </validation-observer>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BFormSelect,
  BFormCheckbox,
  BFormInvalidFeedback,
  BButton,
  BSpinner,
} from 'bootstrap-vue';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { ref, computed, watch } from '@vue/composition-api';
import formValidation from '@core/comp-functions/forms/form-validation';
import store from '@/store';
import { FormInput } from '@/views/apps/fbr-invoice/components/form';
import HSCodeDropdownField from '@/views/apps/fbr-invoice/components/form/HSCodeDropdownField.vue';
import UOMDropdownField from '@/views/apps/fbr-invoice/components/form/UOMDropdownField.vue';
import ItemGroupDropdownField from '@/views/apps/fbr-invoice/components/form/ItemGroupDropdownField.vue';
import { generateRandomString } from '@/utils/functions';

export default {
  name: 'ItemsForm',
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormSelect,
    BFormCheckbox,
    BFormInvalidFeedback,
    BButton,
    BSpinner,

    // Form Validation
    ValidationObserver,
    ValidationProvider,

    // Custom Components
    FormInput,
    HSCodeDropdownField,
    UOMDropdownField,
    ItemGroupDropdownField,
  },
  model: {
    prop: 'formData',
    event: 'update:form-data',
  },
  props: {
    formData: {
      type: Object,
      required: false,
      default: null,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    isModal: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit, root }) {
    const businessID = computed(() => store.state.user.businessID);

    // Loading states for dropdowns
    const isLoadingUnits = ref(false);
    const isLoadingItemGroups = ref(false);
    const isLoadingHSCodes = ref(false);

    // Dropdown options
    const unitOptions = ref([]);
    const itemGroupOptions = ref([]);
    const hsCodeOptions = ref([]);

    const itemsFormData = ref(
      props.formData
        ? JSON.parse(JSON.stringify(props.formData))
        : {
            id: 0,
            itemName: '',
            itemCode: generateRandomString(),
            unitID: '',
            itemGroupID: '',
            itemHSCodeID: '',
            itemPurchaseRate: 0,
            tradePrice: 0,
            retailPrice: 0,
            salesTaxPercent: 0,
            addSalesTaxPercent: 0,
            isActive: true,
            businessID: businessID.value,
          }
    );

    const onSubmit = async () => {
      emit('update:form-data', itemsFormData.value);
      emit('submit', itemsFormData.value);
    };

    const { refFormObserver, getValidationState, resetForm } = formValidation(() => {
      itemsFormData.value = {
        id: 0,
        itemCode: generateRandomString(),
        itemName: '',
        unitID: '',
        itemGroupID: '',
        itemHSCodeID: '',
        itemPurchaseRate: 0,
        tradePrice: 0,
        retailPrice: 0,
        salesTaxPercent: 0,
        addSalesTaxPercent: 0,
        isActive: true,
        addedBy: 0,
        addedOn: '',
        updatedBy: 0,
        updatedOn: '',
        metaAddedBy: '',
        metaUpdatedBy: '',
        meta_unit: '',
        meta_itemGroup: '',
        meta_HSCode: '',
        businessID: businessID.value,
        isDeleted: false,
        deletedByUserID: 0,
      };
    });

    // Watch for changes in the formData prop and update local form data
    watch(
      () => props.formData,
      (newFormData) => {
        if (newFormData) {
          itemsFormData.value = JSON.parse(JSON.stringify(newFormData));
        }
      },
      { immediate: true, deep: true }
    );

    return {
      itemsFormData,
      unitOptions,
      itemGroupOptions,
      hsCodeOptions,
      isLoadingUnits,
      isLoadingItemGroups,
      isLoadingHSCodes,
      onSubmit,
      refFormObserver,
      getValidationState,
      resetForm,
    };
  },
};
</script>

<style lang="scss" scoped>
.b-card {
  margin-bottom: 1rem;
}

// Sticky header styling
.resource-header {
  background: white;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .actions-container {
    width: 100%;

    h4 {
      color: #2c3e50;
      font-weight: 600;
    }

    small {
      color: #6c757d;
    }
  }
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 1000;
}
</style>
